import React, { useState, useEffect, useRef } from 'react';
import { FiZap, FiRefreshCw, FiCheck, FiSquare, FiSave } from 'react-icons/fi';
import { fullStoryService } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { StepCard, Notice, countWords, WORDS_PER_SECOND, showError } from './shared';
// Estimates use the pace of the voice this user reads with (measured on the server), else the default.

const LENGTH_PRESETS = [60, 120, 180, 300, 600, 1200]; // 1, 2, 3, 5, 10, 20 minutes; any other length in seconds below

/** Step 1 — raw material in, narration out (written the way Quick AI writes), then edit / redo / approve. */
export const ScriptStep = ({ story, options, run, readOnly, setStory }) => {
  const t = useT();
  const id = story._id;
  const [input, setInput] = useState(story.script.input);
  const [instructions, setInstructions] = useState(story.script.instructions);
  const [text, setText] = useState(story.script.text);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [seconds, setSeconds] = useState(String(story.targetSeconds));
  const abortRef = useRef(null);

  // Take the server's script when it changes and the user has no unsaved edit.
  useEffect(() => {
    if (!dirty && !streaming) setText(story.script.text);
  }, [story.script.text, dirty, streaming]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const words = countWords(text);
  const estimate = words / (story.wordsPerSecond || WORDS_PER_SECOND);
  const tooLong = estimate > story.targetSeconds * 1.1;
  const voiced = story.voice.sentences.some((s) => s.audioFile);

  const saveSeconds = () => {
    const n = Math.round(Number(seconds));
    if (!Number.isFinite(n) || n < 10 || n > options.maxSeconds) {
      showError(null, t('fullStory.lengthRange', { max: options.maxSeconds }));
      setSeconds(String(story.targetSeconds));
      return;
    }
    if (n !== story.targetSeconds) run(() => fullStoryService.update(id, { targetSeconds: n }));
  };

  const write = async (revisionNote) => {
    if (dirty && !window.confirm(t('fullStory.discardEdits'))) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    setDirty(false);
    let draft = '';
    setText('');
    await fullStoryService.streamScript(
      id,
      revisionNote ? { note: revisionNote } : { input, instructions },
      {
        signal: controller.signal,
        onChunk: ({ text: piece }) => { draft += piece; setText(draft); },
        onDone: ({ story: next }) => { setStory(next); setNote(''); },
        onError: (e) => showError(e, t('fullStory.scriptFailed')),
      }
    );
    abortRef.current = null;
    setStreaming(false);
    fullStoryService.get(id).then(setStory).catch(() => {});
  };

  const saveEdits = () => run(async () => {
    const next = await fullStoryService.saveScript(id, text);
    setDirty(false);
    return next;
  });

  const approve = async () => {
    if (dirty) {
      const saved = await run(() => fullStoryService.saveScript(id, text));
      if (!saved) return;
      setDirty(false);
    }
    if (voiced && (dirty || !story.script.approved) && !window.confirm(t('fullStory.approveScriptRedo'))) return;
    run(() => fullStoryService.approveScript(id));
  };

  return (
    <StepCard
      title={t('fullStory.script.title')}
      description={t('fullStory.script.desc')}
      footer={!readOnly && (
        <>
          <p className="text-xs text-gray-500">{story.script.approved && !dirty ? t('fullStory.script.approvedNote') : t('fullStory.script.approveHint')}</p>
          <button onClick={approve} disabled={streaming || !text.trim()} className="btn btn-primary flex items-center gap-2 disabled:opacity-50">
            <FiCheck size={15} /> {t('fullStory.script.approve')}
          </button>
        </>
      )}
    >
      {/* Length and shape */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <label htmlFor="fs-seconds" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('fullStory.length')}</label>
          <div className="flex flex-wrap items-center gap-1.5">
            {LENGTH_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                disabled={readOnly || streaming}
                onClick={() => { setSeconds(String(n)); if (n !== story.targetSeconds) run(() => fullStoryService.update(id, { targetSeconds: n })); }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${story.targetSeconds === n ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'} disabled:opacity-50`}
              >
                {t('fullStory.minutes', { n: n / 60 })}
              </button>
            ))}
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <input
                id="fs-seconds"
                type="number"
                min="10"
                max={options.maxSeconds}
                value={seconds}
                disabled={readOnly || streaming}
                onChange={(e) => setSeconds(e.target.value)}
                onBlur={saveSeconds}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="input w-20 py-1 text-xs tabular-nums"
              />
              {t('fullStory.secondsMax', { max: options.maxSeconds })}
            </span>
          </div>
        </div>
        <div>
          <span id="fs-shape" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('fullStory.shape')}</span>
          <div role="radiogroup" aria-labelledby="fs-shape" className="flex p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
            {['16:9', '9:16'].map((ratio) => (
              <button
                key={ratio}
                role="radio"
                aria-checked={story.aspectRatio === ratio}
                disabled={readOnly || streaming}
                onClick={() => {
                  if (ratio === story.aspectRatio) return;
                  if (story.scenes.some((s) => s.image?.source === 'ai') && !window.confirm(t('fullStory.shapeChangeWarn'))) return;
                  run(() => fullStoryService.update(id, { aspectRatio: ratio }));
                }}
                className={`px-3 py-1 rounded-md ${story.aspectRatio === ratio ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {ratio === '16:9' ? t('fullStory.landscape') : t('fullStory.portrait')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Material */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fs-input" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('fullStory.script.material')}</label>
          <textarea
            id="fs-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            readOnly={readOnly}
            rows={9}
            placeholder={t('fullStory.script.materialPlaceholder')}
            className="input w-full text-sm leading-relaxed resize-y"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="fs-instructions" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('fullStory.script.instructions')}</label>
          <textarea
            id="fs-instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            readOnly={readOnly}
            rows={5}
            maxLength={2000}
            placeholder={t('fullStory.script.instructionsPlaceholder')}
            className="input w-full text-sm leading-relaxed resize-y flex-1"
          />
          {!readOnly && (
            <div className="mt-3 flex justify-end">
              {streaming ? (
                <button onClick={() => abortRef.current?.abort()} className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <FiSquare size={13} /> {t('fullStory.stop')}
                </button>
              ) : (
                <button onClick={() => write()} disabled={!input.trim() && !instructions.trim()} className="btn btn-primary flex items-center gap-2 disabled:opacity-50">
                  <FiZap size={14} /> {story.script.text ? t('fullStory.script.writeAgain') : t('fullStory.script.write')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {story.script.status === 'failed' && story.script.error && <Notice tone="error">{story.script.error}</Notice>}

      {/* The script */}
      {(text || streaming) && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label htmlFor="fs-script" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('fullStory.script.narration')}</label>
            <span className={`text-xs tabular-nums ${tooLong ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
              {t('fullStory.script.estimate', { words, seconds: Math.round(estimate), target: story.targetSeconds })}
            </span>
          </div>
          <textarea
            id="fs-script"
            value={text}
            onChange={(e) => { setText(e.target.value); setDirty(true); }}
            readOnly={readOnly || streaming}
            rows={12}
            className="input w-full text-[15px] leading-relaxed resize-y"
            style={{ fontFamily: '"Noto Sans Telugu", sans-serif' }}
          />
          {tooLong && !streaming && <p className="text-xs text-amber-700 mt-1">{t('fullStory.script.tooLong')}</p>}

          {!readOnly && !streaming && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {dirty && (
                <button onClick={saveEdits} className="btn btn-ghost text-sm flex items-center gap-1.5">
                  <FiSave size={13} /> {t('fullStory.saveEdits')}
                </button>
              )}
              <div className="flex-1 min-w-[260px] flex gap-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) write(note.trim()); }}
                  maxLength={2000}
                  placeholder={t('fullStory.script.redoPlaceholder')}
                  className="input flex-1 text-sm"
                />
                <button onClick={() => write(note.trim())} disabled={!note.trim() || !story.script.text} className="btn btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-40 whitespace-nowrap">
                  <FiRefreshCw size={13} /> {t('fullStory.script.redo')}
                </button>
              </div>
              {story.script.versions?.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    const index = Number(e.target.value);
                    if (Number.isInteger(index) && window.confirm(t('fullStory.script.restoreConfirm'))) {
                      setDirty(false);
                      run(() => fullStoryService.restoreScript(id, index));
                    }
                  }}
                  className="input text-xs py-1.5 w-auto"
                  aria-label={t('fullStory.script.earlierDrafts')}
                >
                  <option value="">{t('fullStory.script.earlierDrafts')} ({story.script.versions.length})</option>
                  {story.script.versions.map((v, i) => ({ v, i })).reverse().map(({ v, i }) => (
                    <option key={i} value={i}>
                      {new Date(v.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — {v.text.slice(0, 40)}…
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </StepCard>
  );
};
